/* eslint-disable react-hooks/exhaustive-deps */
import { Locale } from "@common/constans";
import { TripType } from "@common/models";
import { IViewport } from "@config/appConfig";
import { Culture, DateTime, ILocale } from "@core/index";
import { IAppState } from "@misc/State";
import Config from "@service/Config";
import Translate from "@service/Translate";
import { FC, MouseEvent, ReactNode, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Divider from "./Divider";
import Button from "./form/Button";
import { IDateRange } from "./form/DateRangeInput";
import Icon from "./Icon";
import TextWrapper from "./TextWrapper";

interface IRangePickerProps {
    endRange: string;
    isFirst: boolean;
    maxDateValue?: number;
    minDateValue?: number;
    value?: string;
    options: IDateRange;
    singleSelect: boolean;
    startRange: string;
    gridCount: number;
    onAbort: () => void;
    onSubmit: () => void;
    onChange: (name: string, value: any) => void;
    onFirstChange: (isFirst: boolean) => void;
    onDateRangeModeChange: () => void;
    onFlightTypeChange?: (name: string, value: any) => void;
}

const RangePicker: FC<IRangePickerProps> = (props) => {
    const { tr } = Translate;
    const { small, medium } = Config.get<IViewport>("viewport");
    const IrLocale = Culture.getLocale(Locale.fa);
    const { maxDateValue, minDateValue, onFlightTypeChange } = props;
    const { availabilityRequest, localeState, width } = useSelector((state: IAppState) => ({
        availabilityRequest: state.availabilityRequest,
        localeState: state.locale,
        width: state.windowWidth
    }));
    let [dateTime, setDateTime] = useState<DateTime>(Culture.getDateTimeInstance(localeState));
    let [selectedFirstDateTime, setSelectedFirstDateTime] = useState<DateTime>();
    let [selectedLastDateTime, setSelectedLastDateTime] = useState<DateTime>();
    let [selectedHoverDateTime, setSelectedHoverDateTime] = useState<DateTime>(Culture.getDateTimeInstance(localeState));
    let [locale, setLocale] = useState<ILocale>(dateTime.locale);
    let [dateTimeFormat, setDateTimeFormat] = useState<string>(locale.defaultDateFormat);
    let [monthNames, setMonthNames] = useState<string[]>(locale.months.map(x => x.displayText));
    const [hover, setHover] = useState({ visible: false, top: 0, left: 0, title: "", class: "hover-blue" });
    const [, updateState] = useState<any>();
    const forceUpdate = useCallback(() => updateState({}), []);
    const weekDays = dateTime.locale.weekDays.map((w, i) => <th key={i}>{w.abbreviation}</th>);

    const init = (startRange: string, endRange: string) => {
        dateTime = Culture.getDateTimeInstance(localeState);
        selectedFirstDateTime = Culture.getDateTimeInstance(localeState);
        selectedLastDateTime = Culture.getDateTimeInstance(localeState);
        if (selectedFirstDateTime.validate(startRange, false, false)) {
            if (width > small) {
                dateTime.setTime(selectedFirstDateTime.getTime());
            } else {
                setTimeout(() => {
                    const focusElement = document.getElementsByClassName("first-select");
                    if (focusElement.length)
                        focusElement[0].parentElement?.parentElement?.parentElement?.parentElement?.scrollIntoView({ block: "start" });
                }, 500);
            }
        } else {
            selectedFirstDateTime = undefined;
        }
        if (!selectedLastDateTime.validate(endRange, false, false))
            selectedLastDateTime = undefined;

        setDateTime(dateTime);
        setSelectedFirstDateTime(selectedFirstDateTime);
        setSelectedLastDateTime(selectedLastDateTime);
        forceUpdate();
    }

    useEffect(() => {
        const date = Culture.getDateTimeInstance(localeState);
        setLocale(date.locale);
        setDateTimeFormat(date.locale.defaultDateFormat);
        setMonthNames(date.locale.months.map(x => x.displayText));
        init(props.startRange, props.endRange);
    }, [localeState, props.singleSelect]);

    const nextMonth = () => {
        dateTime.setMonth(dateTime.getMonth() + 1, 1);
        setDateTime(dateTime)
        forceUpdate();
    }

    const prevMonth = () => {
        dateTime.setMonth(dateTime.getMonth() - 1, 1);
        setDateTime(dateTime)
        forceUpdate();
    }

    // const nextYear = () => {
    //     firstDateTime.setFullYear(firstDateTime.getFullYear() + 1);
    //     lastDateTime.setFullYear(lastDateTime.getFullYear() + 1);
    //     setFirstDateTime(firstDateTime);
    //     setLastDateTime(lastDateTime);
    //     forceUpdate();
    // }

    // const prevYear = () => {
    //     firstDateTime.setFullYear(firstDateTime.getFullYear() - 1);
    //     lastDateTime.setFullYear(lastDateTime.getFullYear() - 1);
    //     setFirstDateTime(firstDateTime);
    //     setLastDateTime(lastDateTime);
    //     forceUpdate();
    // }

    // const onYearSelect = () => {
    //     // tslint
    // }

    // const onMonthSelect = () => {
    //     // tslint
    // }

    const onDayMouseOver = (e: MouseEvent<HTMLTableCellElement>, hoverDateTime: DateTime) => {
        const position = e.currentTarget.getBoundingClientRect();
        selectedHoverDateTime.setTime(hoverDateTime.getTime());
        setSelectedHoverDateTime(selectedHoverDateTime);
        setHover({
            visible: true,
            top: position.y - 30,
            left: position.x - 5,
            title: props.isFirst ? props.options.departure.label || '' : (selectedFirstDateTime?.getTime() ?? 0) > selectedHoverDateTime.getTime() ? props.options.departure.label || '' : props.options.arrival.label || '',
            class: props.isFirst ? "hover-blue" : (selectedFirstDateTime?.getTime() ?? 0) > selectedHoverDateTime.getTime() ? "hover-blue" : "hover-green"
        });
    }

    const onDayMouseOut = (e: MouseEvent<HTMLTableCellElement>) => {
        setHover({
            visible: false,
            top: 0,
            left: 0,
            title: "",
            class: ""
        })
    }

    const onDaySelect = (selectDate: DateTime) => {
        let isFirst = props.isFirst;
        if (props.singleSelect) {
            selectedFirstDateTime = Culture.getDateTimeInstance(localeState).setTime(selectDate.getTime());
            setSelectedFirstDateTime(selectedFirstDateTime);
            props.onChange("startRange", selectedFirstDateTime.format(dateTimeFormat, false));
        } else {
            if (isFirst) {
                selectedFirstDateTime = Culture.getDateTimeInstance(localeState).setTime(selectDate.getTime());
                setSelectedFirstDateTime(selectedFirstDateTime);
                props.onChange("startRange", selectedFirstDateTime.format(dateTimeFormat, false));
                if (props.endRange) {
                    if ((selectedFirstDateTime?.getTime() ?? 0) > (selectedLastDateTime?.getTime() ?? 0)) {
                        setSelectedLastDateTime(undefined);
                        props.onChange("endRange", "");
                    }
                }
            } else {
                if (selectDate.getTime() < (selectedFirstDateTime?.getTime() ?? 0)) {
                    selectedFirstDateTime = Culture.getDateTimeInstance(localeState).setTime(selectDate.getTime());
                    setSelectedFirstDateTime(selectedFirstDateTime);
                    props.onChange("startRange", selectedFirstDateTime.format(dateTimeFormat, false));
                    isFirst = true;
                } else {
                    selectedLastDateTime = Culture.getDateTimeInstance(localeState).setTime(selectDate.getTime());
                    props.onChange("endRange", selectedLastDateTime.format(dateTimeFormat, false));
                    setSelectedLastDateTime(selectedLastDateTime);
                }
            }
            props.onFirstChange(!isFirst);
        }
    }

    const onSubmit = () => {
        props.onSubmit();
    }

    const onAddReturnDateClick = () => {
        if (onFlightTypeChange) {
            onFlightTypeChange("tripType", TripType.RoundTrip);
            props.onFirstChange(props.startRange ? false : true);
        }
    }
    const onCloseReturnDateClick = () => {
        if (onFlightTypeChange) {
            onFlightTypeChange("tripType", TripType.OneWay);
            props.onFirstChange(props.startRange ? false : true);
        }
    }

    const renderWeekDays = (dateTime: DateTime) => {
        const nowDateTime = Culture.getDateTimeInstance(localeState);
        const today = nowDateTime.getDate();
        const isThisMonth = nowDateTime.getFullYear() === dateTime.getFullYear() && nowDateTime.getMonth() === dateTime.getMonth();
        const isSelectedFirstMonth = selectedFirstDateTime?.getFullYear() === dateTime.getFullYear() && selectedFirstDateTime.getMonth() === dateTime.getMonth();
        const isSelectedLastMonth = selectedLastDateTime?.getFullYear() === dateTime.getFullYear() && selectedLastDateTime.getMonth() === dateTime.getMonth();
        const selectedFirstDay = selectedFirstDateTime?.getDate();
        const selectedLastDay = selectedLastDateTime?.getDate();
        const rows = [];
        let rowCounter = 1;
        let colCounter = 0;
        const daysInMonth = dateTime.getDaysInMonth(dateTime.getMonth());
        let row = [];
        // get weekDay first day of month
        const firstWeekDayOfMonth = dateTime.getDay();
        // first row
        for (let i = 0; i < firstWeekDayOfMonth; ++i) {
            row.push(<td key={colCounter}>&nbsp;</td>);
            ++colCounter;
        }
        for (let i = 1; i <= daysInMonth; i++) {
            let className = "day";
            className = `${className}`;
            className = `${className}${isThisMonth && i === today ? " today" : ""}`;
            className = `${className}${isSelectedFirstMonth ? (i === selectedFirstDay ? " selected first-select" : "") : ''}`;
            className = `${className}${isSelectedLastMonth ? (i === selectedLastDay ? " selected last-select" : "") : ''}`;
            const selectDateTime = Culture.getDateTimeInstance(localeState);
            selectDateTime.setFullYear(dateTime.getFullYear(), dateTime.getMonth(), i);
            // add class for focused
            if ((selectedFirstDateTime?.getTime() ?? 0) < (selectedLastDateTime?.getTime() ?? 0))
                if (selectDateTime.getTime() >= (selectedFirstDateTime?.getTime() ?? 0) && selectDateTime.getTime() <= (selectedLastDateTime?.getTime() ?? 0))
                    className = `${className} focused`;
            // add class for disable
            if (minDateValue && selectDateTime.getTime() < minDateValue)
                className = `${className} disable`;
            else if (maxDateValue && selectDateTime.getTime() > maxDateValue)
                className = `${className} disable`;
            // add class for hover
            else if (hover.visible) {
                if (props.startRange) {
                    if (props.endRange && selectedHoverDateTime.getTime() <= (selectedFirstDateTime?.getTime() ?? 0)) {
                        if (selectDateTime.getTime() <= (selectedFirstDateTime?.getTime() ?? 0) && selectDateTime.getTime() >= selectedHoverDateTime.getTime()) {
                            className = `${className} hovered`;
                            className = `${className}${selectDateTime.getTime() === selectedHoverDateTime.getTime() ? " first-hover" : ""}`;
                        }
                    } else if (!props.endRange && selectedHoverDateTime.getTime() >= (selectedFirstDateTime?.getTime() ?? 0)) {
                        if (selectDateTime.getTime() >= (selectedFirstDateTime?.getTime() ?? 0) && selectDateTime.getTime() <= selectedHoverDateTime.getTime()) {
                            className = `${className} hovered`;
                            className = `${className}${selectDateTime.getTime() === selectedFirstDateTime?.getTime() ? " first-hover" : ""}`;
                            className = `${className}${selectDateTime.getTime() === selectedHoverDateTime.getTime() ? " last-hover" : ""}`;
                        }
                    }
                }
                if (props.endRange && !props.isFirst && selectedHoverDateTime.getTime() >= (selectedLastDateTime?.getTime() ?? 0)) {
                    if (selectDateTime.getTime() >= (selectedLastDateTime?.getTime() ?? 0) && selectDateTime.getTime() <= selectedHoverDateTime.getTime()) {
                        className = `${className} hovered`;
                        className = `${className}${selectDateTime.getTime() === selectedHoverDateTime.getTime() ? " last-hover" : ""}`;
                    }
                }
            }

            row.push(
                <td
                    key={colCounter}
                    className={className}
                    onClick={() => (className.indexOf('disable') > -1) ? undefined : onDaySelect(selectDateTime)}
                    onMouseOver={(e) => width <= medium || (className.indexOf('disable') > -1) || props.singleSelect ? undefined : onDayMouseOver(e, selectDateTime)}
                    onMouseOut={(e) => width <= medium || (className.indexOf('disable') > -1) || props.singleSelect ? undefined : onDayMouseOut(e)}
                >
                    <i>{i}</i>
                </td>
            );
            ++colCounter;
            if (colCounter % 7 === 0) {
                rows.push(<tr key={rowCounter++}>{row}</tr>);
                row = [];
            }
        }
        // next month remaining cell
        if (colCounter % 7)
            for (let i = colCounter % 7; i < 7; ++i) {
                row.push(<td key={colCounter++}>&nbsp;</td>);
            }

        if (row.length)
            rows.push(<tr key={rowCounter}>{row}</tr>);

        if (rows.length < 6 && width > small)
            rows.push(<tr key={++rowCounter}><td colSpan={7} /></tr>);

        return rows;
    }

    const renderMonth = () => {
        const monthItems: ReactNode[] = [];
        const selectDate = Culture.getDateTimeInstance(localeState).setTime(dateTime.getTime());
        selectDate.setDate(1);
        for (let i = 0; i < props.gridCount; i++) {
            monthItems.push(
                <div key={`range-picker-item-${i}`} className="range-picker-item">
                    <div className="select month-select">
                        {width > small && i === 0 && <button onClick={prevMonth}>&#8249;</button>}
                        <div className="month-title">{monthNames[selectDate.getMonth()]}</div>
                        {width > small && i === props.gridCount - 1 && <button onClick={nextMonth}>&#8250;</button>}
                    </div>
                    <table>
                        <thead>
                            <tr className="week-days-name">
                                {weekDays}
                            </tr>
                        </thead>
                        <tbody>
                            {renderWeekDays(selectDate)}
                        </tbody>
                    </table>
                </div>
            )
            selectDate.setMonth(selectDate.getMonth() + 1);
        }
        return monthItems;
    }

    return (
        <div className="range-picker">
            <div className="picker-wrapper">
                <div className="picker-header">
                    <div className="year-select">
                        <div className="row flex spa-1">
                            <div className="col-1-3 flex flex-center align-center close-date-picker-btn">
                                <Button
                                    color="white"
                                    width="30px"
                                    height="30px"
                                    icon="icon-close"
                                    className="close-date-picker-btn"
                                    onClick={() => { if (props.onAbort) props.onAbort() }}
                                />
                                <div className="year-title">{dateTime.getFullYear()}</div>
                            </div>
                            <div className="col-1-3 flex align-center type-date-picker">
                                <div className="type-title">{localeState === IrLocale.code ? tr("calendar_persian") : tr("calendar_gregorian")}</div>
                                <div className="year-title">{dateTime.getFullYear()}</div>
                            </div>
                            <div className="col-1-3 flex align-center change-date-picker-btn">
                                <Button
                                    color="white"
                                    variant="outlined"
                                    width="auto"
                                    text={localeState === IrLocale.code ? tr("calendar_change_to_gregorian") : tr("calendar_change_to_persian")}
                                    onClick={props.onDateRangeModeChange}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="date-show">
                        <span>{dateTime.getDay()}</span>
                        <span>{monthNames[dateTime.getMonth()]}</span>
                        <span>{dateTime.getFullYear()}</span>
                    </div>
                </div>
                <div className={`range-picker-container ${localeState === IrLocale.code ? "persian-range-picker" : "gregorian-range-picker"}`}>
                    {renderMonth()}
                    {
                        hover.visible ?
                            <div className="range-picker-hover" style={{ top: hover.top, left: hover.left }}>
                                <div className={`range-picker-hover-title ${hover.class}`}>{hover.title}</div>
                                <div className="range-picker-hover-cursor"></div>
                            </div> : ""
                    }
                </div>
                <div id="range-picker-btn" className="range-picker-btn">
                    <div className="flex range-picker-footer-detail">
                        <div className={`range-picker-detail-item range-picker-start-range${props.isFirst ? " active" : ""}`}>
                            <Icon name={`${props.options.departure.icon ?? "icon-calendar-outline"} blue ex-sm`} />
                            <span className="range-picker-footer-detail-title blue ex-sm">{props.options.departure.label}</span>
                            <TextWrapper
                                className="range-picker-footer-detail-value"
                                text={props.startRange ? localeState === Locale.fa ? selectedFirstDateTime?.format(`l، Y/m/d`, false) || "" : selectedFirstDateTime?.format(`l, d M Y`, false) || "" : ""}
                                locale={localeState}
                                oppositeDirection={Culture.getLocale().code !== localeState}
                            />
                        </div>
                        {
                            width < medium && availabilityRequest.tripType !== TripType.MultiCity &&
                            <Divider
                                orientation="vertical"
                                mode="default"
                                style={{ height: "100%" }}
                            />
                        }
                        {
                            props.singleSelect ?
                                onFlightTypeChange ?
                                    <div className="range-picker-detail-item range-picker-end-range">
                                        <Button
                                            id="addReturnDate"
                                            className="add-return-date"
                                            variant="outlined"
                                            color="primary"
                                            text={tr("add_return_date")}
                                            icon="icon-plus"
                                            onClick={onAddReturnDateClick}
                                        />
                                    </div> : ""
                                :
                                <div className={`range-picker-detail-item range-picker-end-range${props.isFirst ? "" : " active"}`}>
                                    <Icon name={`${props.options.arrival.icon ?? "icon-calendar-outline"} teal ex-sm`} />
                                    <span className="range-picker-footer-detail-title teal ex-sm">{props.options.arrival.label}</span>
                                    <TextWrapper
                                        className="range-picker-footer-detail-value"
                                        text={props.endRange ? localeState === Locale.fa ? selectedLastDateTime?.format(`l، Y/m/d`, false) ?? "" : selectedLastDateTime?.format(`l, d M Y`, false) ?? "" : ""}
                                        locale={localeState}
                                        oppositeDirection={Culture.getLocale().code !== localeState}
                                    />
                                    <Button
                                        id="addReturnDate"
                                        className="close-return-date flex center"
                                        variant="outlined"
                                        icon="icon-close"
                                        onClick={onCloseReturnDateClick}
                                    />
                                </div>
                        }
                    </div>
                    <Button
                        id="dateSelect"
                        className="date-select"
                        variant="contained"
                        text={tr("btn_select")}
                        color="danger"
                        height="35px"
                        disabled={(!props.singleSelect) && (!props.endRange) ? true : false}
                        onClick={onSubmit}
                    />
                </div>
            </div>
        </div>
    )
}

export default RangePicker;